/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\components\ModalSelectionForme.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

import React, { useState } from 'react';

interface ModalSelectionFormeProps {
  isOpen: boolean;
  onClose: () => void;
  onFormeSelected: (forme: string) => void;
  typeArticle: 'coussin' | 'bache';
  formeActuelle?: string;
}

interface FormeOption {
  id: string;
  nom: string;
  icone: string;
  apercu: string;
  description: string;
}

const FORMES_COUSSINS: FormeOption[] = [
  {
    id: 'rectangulaire_bandeau',
    nom: 'Rectangle avec bandeau',
    icone: '/images/formes/coussins/rectanglulaire_bandeau.ico',
    apercu: '/images/formes/coussins/rectanglulaire_bandeau.png',
    description: 'Dessus rectangulaire avec bandeau périphérique'
  },
  {
    id: 'rectangulaire_demi_bandeau',
    nom: 'Rectangle demi-bandeau',
    description: 'Plateau rectangulaire avec demi-bandeau latéral',
    icone: '/images/formes/coussins/rectanglulaire_demi_bandeau.ico',
    apercu: '/images/formes/coussins/rectanglulaire_demi_bandeau.png'
  },
  {
    id: 'rectangulaire_joues',
    nom: 'Rectangle avec joues',
    icone: '/images/formes/coussins/rectangle_avec_joues.ico',
    apercu: '/images/formes/coussins/rectanglulaire_joues.png',
    description: 'Dessus rectangulaire avec 2 joues latérales'
  },
  {
    id: 'rectangle_joues_biseau',
    nom: 'Rectangle joues biseau',
    icone: '/images/formes/coussins/Rectangle_joues_biseau.ico',
    apercu: '/images/formes/coussins/Rectangle_joues_biseau.png',
    description: 'Rectangle avec joues trapézoïdales biseautées'
  },
  {
    id: 'rectangle_demi_bandeau_biseau',
    nom: 'Rectangle demi-bandeau biseau',
    icone: '/images/formes/coussins/rectangle_demi_bandeau_biseau.ico',
    apercu: '/images/formes/coussins/rectangle_demi_bandeau_biseau.png',
    description: 'Rectangle avec demi-bandeau et plateau biseauté'
  },
  {
    id: 'rectangle_boite',
    nom: 'Rectangle boîte',
    icone: '/images/formes/coussins/rectangle_boite.ico',
    apercu: '/images/formes/coussins/rectangle_boite.png',
    description: 'Boîte rectangulaire avec couvercle'
  },
  {
    id: 'rond_avec_bandeau',
    nom: 'Rond avec bandeau',
    icone: '/images/formes/coussins/rond_avec_bandeau.ico',
    apercu: '/images/formes/coussins/rond_avec_bandeau.png',
    description: 'Dessus circulaire avec bandeau cylindrique'
  },
  {
    id: 'cylindre_joues',
    nom: 'Cylindre avec joues',
    icone: '/images/formes/coussins/cylindre_avec_joues.ico',
    apercu: '/images/formes/coussins/cylindre_joues.png',
    description: 'Cylindre avec 2 joues circulaires'
  }
];

const FORMES_BACHES: FormeOption[] = [
  {
    id: 'rectangle_simple',
    nom: 'Bâche rectangulaire',
    icone: '/images/formes/baches/Bache.ico',
    apercu: '/images/formes/baches/Bache.png',
    description: 'Bâche rectangulaire simple'
  }
];

const ModalSelectionForme: React.FC<ModalSelectionFormeProps> = ({
  isOpen,
  onClose,
  onFormeSelected,
  typeArticle,
  formeActuelle
}) => {
  const [formeSelectionnee, setFormeSelectionnee] = useState<string>(formeActuelle || '');
  const [apercuVisible, setApercuVisible] = useState<string | null>(null);

  if (!isOpen) return null;

  const formes = typeArticle === 'coussin' ? FORMES_COUSSINS : FORMES_BACHES;

  const handleFormeClick = (formeId: string) => {
    setFormeSelectionnee(formeId);
    
    // Cas spécial pour photogrammétrie : ne pas fermer la modale
    if (formeId === 'photogrammetrie_3d') {
      // Juste sélectionner, ne pas fermer
      return;
    }
    
    // Comportement normal pour les autres formes
    onFormeSelected(formeId);
    onClose();
  };

  const handleMouseEnter = (formeId: string) => {
    setApercuVisible(formeId);
  };

  const handleMouseLeave = () => {
    setApercuVisible(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Sélection de la forme - {typeArticle === 'coussin' ? 'Coussin' : 'Bâche'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Contenu avec layout côte à côte */}
        <div className="p-6">
          <div className="flex gap-8">
            {/* Grille des formes à gauche */}
            <div className="flex-1">
              <div className={`grid gap-4 ${
                typeArticle === 'coussin' 
                  ? 'grid-cols-2 md:grid-cols-2' 
                  : 'grid-cols-1 max-w-xs'
              }`}>
                {formes.map((forme) => (
                  <div
                    key={forme.id}
                    className={`relative cursor-pointer transition-all duration-200 ${
                      formeSelectionnee === forme.id
                        ? 'ring-2 ring-blue-500 bg-blue-50'
                        : 'hover:bg-gray-50'
                    } rounded-lg p-4 border-2 ${
                      formeSelectionnee === forme.id ? 'border-blue-500' : 'border-gray-200'
                    }`}
                    onClick={() => handleFormeClick(forme.id)}
                    onMouseEnter={() => handleMouseEnter(forme.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {/* Icône principale */}
                    <div className="flex flex-col items-center space-y-3">
                      <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center">
                        {forme.id === 'photogrammetrie_3d' ? (
                          <div className="text-4xl md:text-5xl">{forme.icone}</div>
                        ) : (
                          <img
                            src={forme.icone}
                            alt={forme.nom}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              // Fallback si l'icône ne charge pas
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                      
                      {/* Nom de la forme */}
                      <h3 className="text-sm font-medium text-gray-900 text-center">
                        {forme.nom}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-xs text-gray-500 text-center">
                        {forme.description}
                      </p>
                    </div>

                    {/* Indicateur de sélection */}
                    {formeSelectionnee === forme.id && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Aperçu fixe à droite */}
            <div className="w-80 flex-shrink-0">
              <div className="sticky top-0">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
                    Aperçu détaillé
                  </h3>
                  
                  {apercuVisible ? (
                    <div className="flex flex-col items-center space-y-4">
                      <div className="w-64 h-64 flex items-center justify-center bg-white rounded-lg border border-gray-200 p-4">
                        <img
                          src={formes.find(f => f.id === apercuVisible)?.apercu}
                          alt="Aperçu détaillé"
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                      
                      <div className="text-center">
                        <h4 className="font-medium text-gray-900 mb-2">
                          {formes.find(f => f.id === apercuVisible)?.nom}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formes.find(f => f.id === apercuVisible)?.description}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-64 h-64 flex items-center justify-center bg-white rounded-lg border border-gray-200 border-dashed">
                      <div className="text-center text-gray-400">
                        <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <p className="text-sm">
                          Survolez une forme<br />pour voir l'aperçu
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalSelectionForme;
